/**
 * Track Install Intent
 * Handles conversion tracking when user clicks "Launch in Figma" CTA
 */

import { analytics } from './analytics';
import { collectUserSignals, generateFingerprint } from './userSignals';
import { getStoredUTMParameters } from './analytics/utmCapture';

const MATCH_USER_API_URL = 'https://colorscales.io/api/match-user';
const MATCH_USER_TIMEOUT_MS = 2000;

function getRefParam(): string | undefined {
  const ref = new URLSearchParams(window.location.search).get('ref');
  return ref && ref.length > 0 ? ref : undefined;
}

async function storeInstallIntent(
  installIntentId: string,
  anonymousId: string,
  signals: ReturnType<typeof collectUserSignals>,
  fingerprint: string,
  utmParams: ReturnType<typeof getStoredUTMParameters>,
): Promise<boolean> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), MATCH_USER_TIMEOUT_MS);

  try {
    const response = await fetch(MATCH_USER_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'intent',
        installIntentId,
        anonymousId,
        signals,
        fingerprint,
        utmParams,
      }),
      signal: controller.signal,
      keepalive: true,
    });

    analytics.track('install_intent_api_result', {
      success: response.ok,
      status: response.status,
      install_intent_id: installIntentId,
    });

    return response.ok;
  } catch (error: unknown) {
    const err = error as { name?: string; message?: string };
    if (err.name !== 'AbortError') {
      console.warn('[InstallIntent] Failed to store:', error);
      analytics.track('install_intent_api_result', {
        success: false,
        error: err.message ?? 'unknown',
        install_intent_id: installIntentId,
      });
    } else {
      analytics.track('install_intent_api_result', {
        success: false,
        error: 'aborted',
        install_intent_id: installIntentId,
      });
    }
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Track install intent and send to matching API
 * Call this when user clicks "Launch in Figma" button
 */
export async function trackInstallIntent(
  ctaLocation: 'header' | 'hero' | 'features' | 'pricing' | 'footer' | 'final-cta',
  destinationUrl: string
): Promise<void> {
  try {
    const signals = collectUserSignals();
    const fingerprint = generateFingerprint(signals);
    const installIntentId = crypto.randomUUID();
    const anonymousId = analytics.getDistinctId();
    const utmParams = getStoredUTMParameters();
    const pagePath = window.location.pathname;
    const pageLocation = window.location.href;
    const ref = getRefParam();

    const storeSuccess = await storeInstallIntent(
      installIntentId,
      anonymousId,
      signals,
      fingerprint,
      utmParams,
    );

    analytics.trackInstallIntentCreated({
      install_intent_id: installIntentId,
      fingerprint,
      user_signals: {
        timezone: signals.timezone,
        language: signals.language,
        screen_resolution: `${signals.screenWidth}x${signals.screenHeight}`,
      },
      timestamp: signals.timestamp,
      cta_location: ctaLocation,
      page_path: pagePath,
      page_location: pageLocation,
      store_success: storeSuccess,
      anonymous_id: anonymousId || undefined,
      ref,
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
      utm_term: utmParams.utm_term,
      utm_content: utmParams.utm_content,
      referrer: utmParams.referrer,
    });

    analytics.trackPluginInstallClicked({
      destination_url: destinationUrl,
      cta_location: ctaLocation,
    });
  } catch (error) {
    console.warn('[InstallIntent] Tracking error:', error);
  }
}
